import React, { useState, useEffect, useMemo, useRef } from 'react';
import { auth, remoteConfig, db } from '../../firebase/clientApp';
import { fetchAndActivate } from '@firebase/remote-config';
import { useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/clientApp';
import { setDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../firebase/clientApp';

const UserContext = React.createContext();

function getOutseta() {
  if (window['Outseta']) {
    return window['Outseta'];
  } else {
    throw new Error('Outseta is missing, have you added the script to head?');
  }
}

function UserProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState({ loading: true }); // This will display a loader while auth function runs
  const [userDocData, setUserDocData] = useState({ loading: true });
  const newUserInitialized = useRef(false);

  const [currentPlan, setCurrentPlan] = useState(null);

  // Get the outseta instance
  const outsetaRef = useRef(getOutseta());

  // get the http callable functions
  const getFirebaseToken = httpsCallable(functions, 'getAccessToken');
  const copyExampleProperties = httpsCallable(
    functions,
    'copyExampleProperties'
  );

  useEffect(() => {
    //subscribe to user related events
    handleOutsetaUserEvents(updateUser);

    // Get the access token from the callback url
    const accessToken = searchParams.get('access_token');

    if (accessToken) {
      try {
        // pass it along to Outseta
        outsetaRef.current.setAccessToken(accessToken);
        // and clean up
        setSearchParams({});
      } catch (err) {
        console.error('Error setting access token: ', err);
      }
    }

    if (outsetaRef.current.getAccessToken()) {
      // Outseta initalized with an authenticated user.
      if (!user?.uid) setUser({ loading: true });
      updateUser();
    } else {
      // Outseta initalized without an authenticated user.
      setUser(null);
    }

    const confirmationToken = searchParams.get('confirmationToken');

    if (confirmationToken) {
      logEvent(analytics, 'clicked_confirmation_email');
    }

    return () => {
      // Clean up user related event subscriptions
      handleOutsetaUserEvents(() => {});
    };
  }, [searchParams, setSearchParams]);

  const updateUser = async () => {
    // Fetch the current user data from outseta
    const outsetaUser = await outsetaRef.current.getUser();
    const payload = await outsetaRef.current.getJwtPayload();

    if (payload['outseta:planUid']) {
      setCurrentPlan(payload['outseta:planUid']);
    }

    const userDetails = {
      uid: outsetaUser.Uid,
      email: outsetaUser.Email,
      displayName: outsetaUser.FullName,
      emailVerified: true,
    };

    // once we have the user data, we pass it to firebase to get a firebase access token, which we then use to authenticate with firebase.
    // we also update the user doc in firestore with the latest data from outseta
    try {
      if (userDetails.uid === user?.uid) return;
      const data = await getFirebaseToken(userDetails);
      const userCredential = await signInWithCustomToken(auth, data.data);
      const userData = userCredential.user;

      if (
        userCredential._tokenResponse?.isNewUser &&
        !newUserInitialized.current
      ) {
        newUserInitialized.current = true;
        await setDoc(doc(db, 'users', userData?.uid), {
          email: outsetaUser.Email || null,
          userName: userData.FullName || null,
          uid: userData.uid,
          createdAt: Date.now(),
          lastSignIn: Date.now(),
        });

        await copyExampleProperties({ uid: userData?.uid }).catch((err) => {
          console.error('Error copying example properties: ', err);
        });
      } else {
        const docRef = doc(db, 'users', userData.uid);
        getDoc(docRef).then((doc) => {
          if (doc.exists()) {
            if (outsetaUser.Email !== doc.data().email) {
              updateDoc(docRef, {
                email: outsetaUser.Email || null,
                lastSignIn: Date.now(),
              });
            } else {
              updateDoc(docRef, {
                lastSignIn: Date.now(),
              });
            }
            setUserDocData(doc.data());
          } else {
            setDoc(docRef, {
              email: outsetaUser.Email || null,
              userName: outsetaUser.Fullname || null,
              uid: userData.uid,
              createdAt: Date.now(),
              lastSignIn: Date.now(),
            });
          }
        });
      }
      setUser(userData);
    } catch (err) {
      console.error('Error in authentication flow: ', err);
    }
  };

  const handleOutsetaUserEvents = (onEvent) => {
    // Subscribe to user related events
    // with onEvent function
    const outseta = outsetaRef.current;
    outseta.on('subscription.update', onEvent);
    outseta.on('profile.update', onEvent);
    outseta.on('account.update', onEvent);
  };

  const logout = async () => {
    // and remove user state
    outsetaRef.current.setAccessToken('');
    setUser(null);
    auth.signOut();
    if (!window.location.pathname.includes('sharing')) {
      window.location.assign('https://propertycast.io/');
    } else {
      window.location.reload();
    }
  };

  useEffect(() => {
    fetchAndActivate(remoteConfig).catch((err) =>
      console.error('Failed to fetch remote config: ', err)
    );
  }, []);

  useEffect(() => {
    auth.onAuthStateChanged((id) => {
      setUser(id);
      if (!id) return;
      const docRef = doc(db, 'users', id?.uid);
      getDoc(docRef).then((doc) => {
        if (doc.exists()) {
          setUserDocData(doc.data());
        }
      });
    });
  }, []);

  const value = useMemo(
    () => ({ user, userDocData, setUser, logout, currentPlan }),
    [user]
  );
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

function useUser() {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export { UserProvider, useUser };
